/**
 * Content-escrow archive types (core-content cutover).
 *
 * Archive version 'adia-escrow-v1'. The escrow captures every IndexedDB
 * object store present in 'adia-db' (enumerated dynamically, so stores this
 * code does not know about are still captured) plus every localStorage key
 * prefixed 'adia-'. That localStorage sweep is not incidental: it is the only
 * capture path for 'adia-prayer-draft' (an uncommitted prayer in the compose
 * textarea) and 'adia-interrupted-interim' (browser-STT interim text from an
 * interrupted recording), either of which can be the sole copy of prayer text
 * on the device.
 *
 * EXCLUSION (deliberate, documented here per the cutover spec): the
 * 'bsb-books' and 'bsb-meta' stores hold the re-downloadable Berean Standard
 * Bible cache (~5+ MB of scripture JSON), NOT user content. For those two
 * stores the archive records only { name, rowCount, keySetDigest,
 * summaryOnly: true }, with no bulk bytes. Every other store (prayers,
 * audio-queue including raw audio Blobs, bible-bookmarks, bible-highlights,
 * app-meta, bible-progress, keepsake-fences, keepsake-intents, keepsake-meta,
 * keepsake-policy-pins, and any future store) is captured in full.
 *
 * Metadata (observed auth user id/email, origin, buildId, userAgent) is
 * EVIDENCE-ONLY context about the capturing device/session. It is never
 * proof of ownership of the content.
 */

/**
 * `as const` is load-bearing: without it the widening-literal type collapses to
 * `string` the moment it is read into a mutable object-literal property, and
 * every `EscrowManifest` construction fails to typecheck.
 */
export const ARCHIVE_VERSION = 'adia-escrow-v1' as const;
export const ESCROW_CAPTURE_ORIGIN = 'https://watchhim.work' as const;
export const SEMANTIC_EMPTY_EVIDENCE_VERSION =
  'adia-semantic-empty-evidence-v1' as const;

/** Stores whose bulk bytes are excluded (scripture cache, re-downloadable). */
export const SUMMARY_ONLY_STORES: readonly string[] = ['bsb-books', 'bsb-meta'];

/**
 * Every IndexedDB version shipped by the application. Capture is intentionally
 * opaque and lossless: it enumerates the inventory that actually exists, so a
 * historical or partially damaged database can still be saved before repair.
 * This is not authority to restore or infer ownership. Restore remains a
 * separate schema-aware, operator-verified operation.
 */
export const COMPATIBLE_IDB_VERSIONS: readonly number[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
];

/**
 * Closed context identifier contract shared with the private capture ledger.
 * Keep this exact browser-side mirror in lockstep with SAFE_ID_RE in
 * scripts/ops/lib/context-capture-ledger.mjs. The exporter cannot import the
 * Node-only operator module into the application or standalone bundle.
 */
const SAFE_CONTEXT_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const SAFE_STORE_NAME_RE = /^[a-z0-9][a-z0-9_-]{0,127}$/i;

export function isSafeContextId(value: unknown): value is string {
  return typeof value === 'string' && SAFE_CONTEXT_ID_RE.test(value);
}

/** Opaque salvage store names accepted consistently by export and verify. */
export function isSafeStoreName(value: unknown): value is string {
  return typeof value === 'string' && SAFE_STORE_NAME_RE.test(value);
}

/** Error codes surfaced by the escrow pipeline. Never carry content. */
export type EscrowErrorCode =
  | 'mutation-during-capture'
  | 'decrypt-failed'
  | 'idb-open-failed'
  | 'idb-read-failed'
  | 'bad-envelope'
  /**
   * 'adia-db' does not exist in this origin/profile. A versionless
   * indexedDB.open of a MISSING database does not fail, it CREATES the
   * database at version 1, which is both a write from a strictly
   * read-only exporter and a permanent poisoning of the app's upgrade
   * ladder (a later open at the real version sees oldVersion 1, skips
   * the clause that creates the 'prayers' store, and aborts). The
   * exporter therefore aborts that upgrade and reports this instead.
   * Operator reading: the capture is pointed at the wrong origin, the
   * wrong browser profile, a private window, or a container whose
   * storage is separate (an iOS PWA does not share Safari's).
   */
  | 'idb-absent'
  /** The database version is older or newer than this exporter understands. */
  | 'idb-incompatible-version'
  /** A database has an unsafe inventory name. */
  | 'idb-incompatible-schema'
  /** localStorage does not exist or its accessor itself is denied. */
  | 'localstorage-unavailable'
  /** localStorage enumeration/getItem was denied, partial, or unstable. */
  | 'localstorage-read-failed'
  /** Encryption passphrase failed the structural strength floor. */
  | 'weak-passphrase'
  /** Missing or malformed preexisting private-ledger context identifier. */
  | 'invalid-context-id'
  /** Capture was not running in the exact reviewed production origin. */
  | 'capture-origin-mismatch'
  /** An explicit semantic-empty check found recoverable device content. */
  | 'semantic-content-present'
  /** Volatile-only evidence did not name one exact canonical registered set. */
  | 'invalid-volatile-record-set'
  /**
   * The capture found no semantic recovery content. Actual prayers, audio
   * queue rows, bookmarks, highlights, nonempty draft/interim text, or a
   * structurally valid unresolved protection intent count. Metadata, corpus
   * witnesses, fences, pins, and dangling/settled bookkeeping do not. Same
   * operator reading as 'idb-absent'. A vacuous archive verifies green at
   * every digest gate, so it is refused at the source.
   */
  | 'empty-capture'
  /**
   * A row held a value the canonical encoder cannot represent. Encoding
   * it would silently substitute a placeholder tag, and the archive
   * would still verify green with the bytes gone. Fail closed instead.
   * If this ever fires, add a real encoding for that type in
   * canonical.ts (and mirror it in the CLI); never weaken the check.
   */
  | 'unrepresentable-value';

export class EscrowError extends Error {
  code: EscrowErrorCode;
  constructor(code: EscrowErrorCode) {
    super(code);
    this.name = 'EscrowError';
    this.code = code;
  }
}

/** A canonically-encoded structured-clone value (JSON-safe tagged tree). */
export type EncodedValue = unknown;

export interface EscrowRow {
  /** Canonically-encoded IDB key. */
  key: EncodedValue;
  /** Canonically-encoded row value (Blobs become tagged base64 + sha256). */
  value: EncodedValue;
  /** sha256 hex of canonicalStringify({ key, value }). */
  digest: string;
}

export interface EscrowStoreFull {
  name: string;
  rowCount: number;
  /** sha256 hex of canonicalStringify(encoded key array, in store order). */
  keySetDigest: string;
  rows: EscrowRow[];
  /** sha256 hex over { name, rowCount, keySetDigest, rowDigests }. */
  contentDigest: string;
  summaryOnly?: false;
}

export interface EscrowStoreSummary {
  name: string;
  rowCount: number;
  keySetDigest: string;
  summaryOnly: true;
  /** sha256 hex over { name, rowCount, keySetDigest }. */
  contentDigest: string;
}

export type EscrowStore = EscrowStoreFull | EscrowStoreSummary;

export interface EscrowLocalStorageEntry {
  key: string;
  value: string;
}

export interface EscrowLocalStorageCapture {
  /**
   * Present only after complete enumeration and successful getItem for every
   * key. Consequently entries: [] means accessible-and-empty, never denied.
   */
  captureStatus: 'complete';
  entries: EscrowLocalStorageEntry[];
}

export interface EscrowMetadata {
  /** Best-effort observed auth identity. EVIDENCE ONLY, never ownership proof. */
  observedAuthUserId: string | null;
  observedAuthEmail: string | null;
  origin: string | null;
  buildId: string | null;
  idbVersion: number | null;
  userAgent: string | null;
}

export interface EscrowDigests {
  /**
   * Digest over everything EXCEPT contextId/captureStart/captureEnd (and the
   * digests block itself). Two back-to-back captures of an unchanged device
   * must produce identical contentDigests.
   */
  contentDigest: string;
  /** Digest over the full manifest (minus this field). */
  manifestDigest: string;
}

export interface EscrowManifest {
  archiveVersion: typeof ARCHIVE_VERSION;
  contextId: string;
  contextLabel: string;
  captureStart: string;
  captureEnd: string;
  metadata: EscrowMetadata;
  stores: EscrowStore[];
  localStorage: EscrowLocalStorageCapture;
  digests: EscrowDigests;
}

/** NON-SENSITIVE receipt. Must never contain plaintext content or passphrase. */
export interface EscrowReceipt {
  archiveVersion: typeof ARCHIVE_VERSION;
  contextId: string;
  contextLabel: string;
  counts: Record<string, number>;
  ciphertextSha256: string;
  contentDigest: string;
  buildId: string | null;
  manifestDigest: string;
  origin: string | null;
  captureStart: string;
  captureEnd: string;
}

/**
 * Non-content proof for a context whose only recoverable items were already
 * registered as volatile recovery records. This is deliberately separate
 * from an escrow archive: the ordinary exporter must still reject an empty
 * capture, while the ledger needs an executable, hashable success path for a
 * real volatile-only partition.
 */
export interface SemanticEmptyEvidence {
  evidenceVersion: typeof SEMANTIC_EMPTY_EVIDENCE_VERSION;
  contextId: string;
  contextLabel: string;
  captureStart: string;
  captureEnd: string;
  origin: typeof ESCROW_CAPTURE_ORIGIN;
  buildId: string | null;
  contentDigest: string;
  manifestDigest: string;
  exporterResult: 'empty-capture';
  settledVolatileRecordIds: string[];
  settledVolatileRecordCount: number;
  settledVolatileRecordSetDigest: string;
  /** SHA-256 of canonicalStringify(all preceding fields). */
  evidenceDigest: string;
}

/** Encrypted envelope (all-JSON; see crypto.ts for the size tradeoff note). */
export interface EscrowEnvelope {
  v: 1;
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  salt_b64: string;
  iv_b64: string;
  alg: 'A256GCM';
  ct_b64: string;
}

export interface EscrowVerification {
  ok: boolean;
  /** Labels only (e.g. "store:prayers:row:3:digest"). Never content. */
  mismatches: string[];
}
